/**
 * TypeIcon — Tier 0 slice-local primitive (SCRUM-167 epic).
 *
 * Maps a `PropertyType` (the canonical bundle taxonomy) onto an icon
 * name from `@sparta/ui-kit`. Used across the Properties list type
 * tile, the property detail hero, the mobile cards, and the create
 * wizard's Type RadioCard.
 *
 * The BE's `unitCategory` enum is broader than the bundle's
 * `PropertyType` (BE includes `townhome`, `apartment`, `other` that the
 * bundle doesn't enumerate). The mapper in `api/dto-mapper.ts`
 * normalises both sides to the bundle's taxonomy; this primitive
 * accepts ONLY the bundle's `PropertyType` so the typecheck enforces
 * the contract at the boundary.
 *
 * Per ADR `frontend-0004-ui-kit-doctrine.md` second-consumer trigger:
 * Tier 0 today; promote to `widgets/` if Reservations or another slice
 * adopts the same type→icon map.
 */

import * as React from 'react';
import { Icon, type IconName } from '@sparta/ui-kit';

/**
 * The canonical bundle taxonomy from `~/Desktop/design_handoff_properties/
 * DATA_MODEL.md` §1. Kept narrower than the BE's `unitCategory` (which
 * adds `townhome`, `apartment`, `other`) so this primitive's contract
 * stays tight; the DTO mapper coerces unknown BE categories to `Other`.
 */
export type PropertyType =
  | 'Villa'
  | 'Home'
  | 'Condominium'
  | 'Cabin'
  | 'Apartment'
  | 'Townhome'
  | 'Cottage'
  | 'Other';

export interface TypeIconProps {
  readonly type: PropertyType;
  /** Optional override; defaults to 16px per the bundle's list-row tile. */
  readonly size?: number;
}

/**
 * Mapping from bundle type to kit `IconName`. Centralised so the four
 * surfaces that render this icon (list / detail hero / mobile card /
 * wizard RadioCard) stay consistent — a future Reservations slice that
 * picks up the same map should reuse this primitive rather than
 * duplicate the table.
 */
const TYPE_TO_ICON: Readonly<Record<PropertyType, IconName>> = {
  // Mirrors `source/properties.jsx` line 81's mapping:
  //   Villa → home  · Home → home2  · Condominium → building  · Cabin → home
  // Apartment / Townhome / Cottage / Other are not in the bundle's
  // small switch; they fall back to the closest semantic glyph.
  Villa: 'home',
  Home: 'home2',
  Condominium: 'building',
  Cabin: 'home',
  Apartment: 'building',
  Townhome: 'building',
  Cottage: 'home',
  Other: 'home',
};

export function TypeIcon({
  type,
  size = 16,
}: TypeIconProps): React.ReactElement {
  // Decorative — the parent tile/card carries the textual category
  // (e.g. "Villa · San Francisco"). aria-hidden so screen readers
  // don't double-announce.
  return (
    <span aria-hidden="true">
      <Icon name={TYPE_TO_ICON[type]} size={size} />
    </span>
  );
}
